import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { PatientNotesSection } from '@/components/admin/PatientNotesSection';
import { PatientAssignmentsManager } from '@/components/admin/PatientAssignmentsManager';
import { RecordHospitalizationDialog } from '@/components/admin/RecordHospitalizationDialog';
import { HospitalizationHistory } from '@/components/admin/HospitalizationHistory';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHIPAAMasking } from '@/hooks/useHIPAAMasking';
import { 
  ArrowLeft, 
  Calendar, 
  Mail, 
  Phone, 
  Activity, 
  FileText, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  CheckCircle2,
  XCircle,
  Home,
  Stethoscope,
  Building2,
  Ambulance,
  EyeOff,
  Hospital
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { format, subMonths, parseISO } from 'date-fns';

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  dob: string | null;
  created_at: string;
  risk_flags: any;
  is_active: boolean;
  agency_id: string;
}

interface TriageSession {
  id: string;
  started_at: string;
  completed_at: string | null;
  recommendation: string | null;
  score: number | null;
  red_flags: boolean | null;
  explanation_text: string | null;
}

interface RiskTrendData {
  month: string;
  redFlagRate: number;
  erRate: number;
  avgScore: number;
  sessions: number;
}

export default function AdminPatientDetailPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const { getDisplayData } = useHIPAAMasking();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [sessions, setSessions] = useState<TriageSession[]>([]);
  const [riskTrend, setRiskTrend] = useState<RiskTrendData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patientId) {
      loadPatientData();
    }
  }, [patientId]);

  const loadPatientData = async () => {
    if (!patientId) return;

    try {
      // Load patient info
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('id', patientId)
        .maybeSingle();

      if (patientError) throw patientError;
      if (!patientData) {
        navigate('/admin/patients');
        return;
      }

      setPatient(patientData);

      // Load triage sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('triage_sessions')
        .select('*')
        .eq('patient_id', patientId)
        .order('started_at', { ascending: false });

      if (sessionsError) throw sessionsError;
      setSessions(sessionsData || []);

      // Calculate risk trend (last 12 months)
      const trend = calculateRiskTrend(sessionsData || []);
      setRiskTrend(trend);

    } catch (error) {
      console.error('Error loading patient data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRiskTrend = (sessions: TriageSession[]): RiskTrendData[] => {
    const monthlyData: Record<string, { redFlags: number; erCases: number; totalScore: number; count: number }> = {};

    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const monthDate = subMonths(new Date(), i);
      const monthKey = format(monthDate, 'yyyy-MM');
      monthlyData[monthKey] = { redFlags: 0, erCases: 0, totalScore: 0, count: 0 };
    }

    // Populate with session data
    sessions.forEach(session => {
      if (!session.completed_at) return;
      const monthKey = format(parseISO(session.completed_at), 'yyyy-MM');
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].count++;
        if (session.red_flags) monthlyData[monthKey].redFlags++;
        if (session.recommendation === 'ER_NOW') monthlyData[monthKey].erCases++;
        if (session.score) monthlyData[monthKey].totalScore += session.score;
      }
    });

    return Object.entries(monthlyData).map(([month, data]) => ({
      month: format(parseISO(`${month}-01`), 'MMM yyyy'),
      redFlagRate: data.count > 0 ? (data.redFlags / data.count) * 100 : 0,
      erRate: data.count > 0 ? (data.erCases / data.count) * 100 : 0,
      avgScore: data.count > 0 ? data.totalScore / data.count : 0,
      sessions: data.count
    }));
  };

  const getAge = (dob: string | null) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getRecommendationDetails = (rec: string | null) => {
    switch (rec) {
      case 'HOME':
        return { label: 'Home Care', icon: Home, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' };
      case 'PCP':
        return { label: 'See Doctor', icon: Stethoscope, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30' };
      case 'URGENT_CARE':
        return { label: 'Urgent Care', icon: Building2, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' };
      case 'ER_NOW':
        return { label: 'Emergency', icon: Ambulance, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30' };
      default:
        return { label: 'Pending', icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted' };
    }
  };

  const getRiskTrendIndicator = () => {
    if (riskTrend.length < 3) return { icon: Minus, label: 'Insufficient data', color: 'text-muted-foreground' };
    
    const recent = riskTrend.slice(-3);
    const earlier = riskTrend.slice(-6, -3);
    
    const recentAvg = recent.reduce((sum, d) => sum + d.redFlagRate, 0) / recent.length;
    const earlierAvg = earlier.length > 0 ? earlier.reduce((sum, d) => sum + d.redFlagRate, 0) / earlier.length : recentAvg;
    
    if (recentAvg < earlierAvg - 5) {
      return { icon: TrendingDown, label: 'Improving', color: 'text-green-600' };
    } else if (recentAvg > earlierAvg + 5) {
      return { icon: TrendingUp, label: 'Needs attention', color: 'text-red-600' };
    }
    return { icon: Minus, label: 'Stable', color: 'text-amber-600' };
  };

  const completedSessions = sessions.filter(s => s.completed_at);
  const redFlagSessions = sessions.filter(s => s.red_flags);
  const erReferrals = sessions.filter(s => s.recommendation === 'ER_NOW');
  const trendIndicator = getRiskTrendIndicator();

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64 lg:col-span-2" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </AdminLayout>
    );
  }

  if (!patient) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Patient not found</p>
          <Button onClick={() => navigate('/admin/patients')} className="mt-4">
            Back to Patients
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const displayData = patient ? getDisplayData({
    id: patient.id,
    first_name: patient.first_name,
    last_name: patient.last_name,
    email: patient.email,
    phone: patient.phone,
    dob: patient.dob,
  }) : null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/patients')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">
                {displayData?.displayName}
              </h1>
              {displayData?.isMasked && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="gap-1">
                        <EyeOff className="h-3 w-3" />
                        HIPAA Protected
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Patient info masked for HIPAA compliance.<br />Only assigned staff can view full details.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-muted-foreground">Patient Details & History</p>
          </div>
          <div className="flex items-center gap-2">
            <RecordHospitalizationDialog
              patientId={patient.id}
              patientName={`${patient.first_name} ${patient.last_name}`}
              agencyId={patient.agency_id}
              trigger={
                <Button variant="outline" size="sm" className="gap-2">
                  <Hospital className="h-4 w-4" />
                  Record Hospitalization
                </Button>
              }
            />
            {!patient.is_active && (
              <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Patient Info Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Patient Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                    {displayData?.displayFirstName.charAt(0)}{displayData?.displayLastName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">{displayData?.displayName}</p>
                  {patient.dob && (
                    <p className="text-sm text-muted-foreground">Age {getAge(patient.dob)}</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{displayData?.displayEmail}</span>
                </div>
                {patient.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground">{displayData?.displayPhone}</span>
                  </div>
                )}
                {patient.dob && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground">
                      {displayData?.isMasked ? displayData.displayDOB : format(parseISO(patient.dob), 'MMMM d, yyyy')}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">Joined {format(parseISO(patient.created_at), 'MMM d, yyyy')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Health Statistics</CardTitle>
                <div className={`flex items-center gap-2 ${trendIndicator.color}`}>
                  <trendIndicator.icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{trendIndicator.label}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <FileText className="h-6 w-6 mx-auto text-primary mb-2" />
                  <p className="text-2xl font-bold text-foreground">{sessions.length}</p>
                  <p className="text-xs text-muted-foreground">Total Sessions</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <CheckCircle2 className="h-6 w-6 mx-auto text-green-600 mb-2" />
                  <p className="text-2xl font-bold text-foreground">{completedSessions.length}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <AlertTriangle className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                  <p className="text-2xl font-bold text-foreground">{redFlagSessions.length}</p>
                  <p className="text-xs text-muted-foreground">Red Flags</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <Ambulance className="h-6 w-6 mx-auto text-red-600 mb-2" />
                  <p className="text-2xl font-bold text-foreground">{erReferrals.length}</p>
                  <p className="text-xs text-muted-foreground">ER Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Risk Trend (Last 12 Months)
            </CardTitle>
            <CardDescription>
              Red flag rate and ER referral trends over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            {riskTrend.some(d => d.sessions > 0) ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={riskTrend} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12 }} 
                      className="text-muted-foreground"
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      className="text-muted-foreground"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        color: 'hsl(var(--foreground))'
                      }}
                      formatter={(value: number, name: string) => [
                        `${value.toFixed(1)}%`,
                        name === 'redFlagRate' ? 'Red Flag Rate' : 'ER Rate'
                      ]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="redFlagRate" 
                      stroke="hsl(var(--chart-3))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--chart-3))', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="erRate" 
                      stroke="hsl(var(--destructive))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--destructive))', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-muted-foreground">
                <p>No session data available for trend analysis</p>
              </div>
            )}
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-3))' }} />
                <span className="text-sm text-muted-foreground">Red Flag Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(var(--destructive))' }} />
                <span className="text-sm text-muted-foreground">ER Referral Rate</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Session History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Assessment History
            </CardTitle>
            <CardDescription>
              All health assessments for this patient
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No assessment sessions recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => {
                  const recDetails = getRecommendationDetails(session.recommendation);
                  const RecIcon = recDetails.icon;
                  
                  return (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${recDetails.bg}`}>
                          <RecIcon className={`h-5 w-5 ${recDetails.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{recDetails.label}</p>
                            {session.red_flags && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Red Flag
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {session.completed_at 
                              ? format(parseISO(session.completed_at), 'MMM d, yyyy h:mm a')
                              : 'In Progress'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {session.score !== null && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-foreground">Score: {session.score}</p>
                          </div>
                        )}
                        {session.completed_at ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hospitalization History */}
        <HospitalizationHistory patientId={patient.id} />

        {/* Supervisor Assignments */}
        <PatientAssignmentsManager patientId={patient.id} agencyId={patient.agency_id} />

        {/* Clinical Notes */}
        <PatientNotesSection patientId={patient.id} />
      </div>
    </AdminLayout>
  );
}
